import { StyleSheet, View, Platform } from "react-native";
import { useMemo, useState } from "react";
import RaceiQIcon, { RIQIcon } from "../Icon/RaceiQIcon";
import Prompt from "./Prompt";
import variables from "../../../../styles/variables";
import { useResponsive } from "../../../../hooks/useResponsive";
import { useAtom } from "jotai";
import { raceTypeAtom } from "../../../../atoms";
import {
  FlatPromptKey,
  HurdlePromptKey,
  RunnerPromptData,
} from "../../../../types/races/runner";

/**
 * RaceiQIconBar is a component that displays a bar of RaceiQ icons.
 * It hosts several icons which display detailed data prompts when selected.
 *
 * @component
 * @param {IconSize} [iconSize="medium"] - The size of the icons to display.
 * @param {boolean} [includeText=false] - Whether to include text labels below the icons.
 *
 * @returns {JSX.Element} The rendered icon bar component.
 */

const { colors } = variables;

type Props = {
  includeText?: boolean;
  firstRunner?: boolean;
  prompts?: RunnerPromptData;
};

const RaceiQIconBar = ({
  includeText = false,
  firstRunner,
  prompts,
}: Props) => {
  const { isSmallAndDown } = useResponsive();
  const [raceType] = useAtom(raceTypeAtom);

  const fontSize = isSmallAndDown ? "xs" : "sm";

  const [selectedPrompt, setSelectedPrompt] = useState<string | undefined>(
    Object.keys(prompts)[0]
  );

  const getTypedKeys = (prompts: RunnerPromptData) => {
    if (raceType === "Flat") {
      return Object.keys(prompts) as FlatPromptKey[];
    }
    if (raceType === "Hurdle" || raceType === "Jumps") {
      return Object.keys(prompts) as HurdlePromptKey[];
    }
    return Object.keys(prompts);
  };

  const elementSize = isSmallAndDown ? 44 : 52;

  const elements = useMemo(() => getTypedKeys(prompts), [prompts]);

  const prompt = prompts[selectedPrompt];

  return (
    <View style={styles.container}>
      <View style={styles.iconsRow}>
        {elements.map((key, index) => {
          if (!prompts[key]) return;

          const isSelected = selectedPrompt === key;

          return (
            <View
              key={`raceiq-icon-${key}-${index}`}
              style={{
                marginRight: isSmallAndDown ? 2 : 4,
                width: isSmallAndDown ? 44 : 52,
              }}
            >
              <RaceiQIcon
                name={key as RIQIcon}
                size={isSmallAndDown ? "medium" : "large"}
                setSelectedPrompt={setSelectedPrompt}
                index={index}
                includeText={true}
                isSelected={isSelected}
                promptKey={key}
                fontSize={fontSize}
              />
              {isSelected ? (
                <View
                  style={[
                    styles.arrow,
                    {
                      borderLeftWidth: isSmallAndDown ? 3 : 4,
                      borderRightWidth: isSmallAndDown ? 3 : 4,
                      borderBottomWidth: isSmallAndDown ? 5 : 6,
                    },
                    // Always add bottom border for Android so it touches
                    Platform.OS == "android" ? { borderBottomWidth: 6 } : {},
                  ]}
                />
              ) : null}
            </View>
          );
        })}
      </View>
      {prompt?.prompt ? (
        <Prompt fontSize={fontSize}>{prompt?.prompt}</Prompt>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: "flex-start",
    width: "100%",
  },
  iconsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  arrow: {
    width: 0,
    height: 0,
    borderStyle: "solid",
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: colors.palette.neutral.x100,
    left: "44%",
  },
});

export default RaceiQIconBar;
